"use client"

import React, { useEffect, useRef, useMemo } from "react"
import ForceGraph2D from "react-force-graph-2d"
import { forceCollide } from "d3-force"

/**
 * Props:
 * - nodeTypes: [{ name, looseDetails:[{name,type}], strictDetails:[{name,type}] }]
 * - edges: [{ source, target, relationship, looseDetails:[{name,type}], strictDetails:[{name,type}] }]
 * - width, height
 * - colorFor: (node) => string   // kept for compatibility, not used (we use stable palette like Create Schema)
 * - isDark: boolean
 */
export default function GraphCanvas({ nodeTypes, edges, width, height, colorFor: _ignored, isDark }) {
  const graphRef = useRef(null)
  const MAX_NODE_RADIUS = 44
  const MIN_NODE_RADIUS = 14

  const tooltipCardStyle = `
    font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
    font-size: 12.5px;
    line-height: 1.45;
    color: ${isDark ? "#f9fafb" : "#111"};
    background: ${isDark ? "#111827" : "#fff"};
    border: 1px solid ${isDark ? "#374151" : "#e5e7eb"};
    border-radius: 10px;
    padding: 10px 12px;
    box-shadow: 0 8px 24px rgba(0,0,0,.12);
    min-width: 220px;
    max-width: min(80vw, 520px);
    width: max-content;
    white-space: normal;
    overflow-wrap: anywhere;
  `

  const tooltipRowStyle = `
    display: grid;
    grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr) auto;
    gap: 8px;
    align-items: start;
    margin-top: 6px;
  `

  const tooltipKeyStyle = "font-weight:600; overflow-wrap:anywhere;"
  const tooltipTypeStyle = `color:${isDark ? "#d1d5db" : "#4b5563"}; overflow-wrap:anywhere;`
  const tooltipOptionalStyle = `justify-self:start; font-size:11px; color:${isDark ? "#d1d5db" : "#6b7280"}; background:${isDark ? "#1f2937" : "#f3f4f6"}; border-radius:9999px; padding:1px 6px;`
  const tooltipEmptyStyle = `color:${isDark ? "#9ca3af" : "#6b7280"};`

  const toNodeId = (value) => {
    if (value == null) return ""
    if (typeof value === "string") return value
    if (typeof value === "object") return String(value.id || value.name || "")
    return String(value)
  }

  // ---- data mapping (keep your curvature offsets, but format like Create Schema)
  const nodes = useMemo(() => {
    const out = []
    const seen = new Set()

    ;(nodeTypes || []).forEach((t) => {
      const id = toNodeId(t?.name)
      if (!id || seen.has(id)) return
      seen.add(id)
      out.push({ id, group: id })
    })

    // Fail-safe: ensure every edge endpoint exists as node.
    ;(edges || []).forEach((e) => {
      const source = toNodeId(e?.source)
      const target = toNodeId(e?.target)
      if (source && !seen.has(source)) {
        seen.add(source)
        out.push({ id: source, group: source })
      }
      if (target && !seen.has(target)) {
        seen.add(target)
        out.push({ id: target, group: target })
      }
    })

    return out
  }, [nodeTypes, edges])

  const links = useMemo(() => {
    const normalized = (edges || [])
      .map((edge) => ({
        source: toNodeId(edge?.source),
        target: toNodeId(edge?.target),
        relationship: edge?.relationship,
        looseDetails: edge?.looseDetails || [],
        strictDetails: edge?.strictDetails || [],
      }))
      .filter((edge) => edge.source && edge.target)

    const withOffsets = normalized.map((edge, _i, arr) => {
      const samePair = arr.filter(
        e =>
          (e.source === edge.source && e.target === edge.target) ||
          (e.source === edge.target && e.target === edge.source)
      )
      const position = samePair.indexOf(edge)
      let curvature = 0
      if (edge.source === edge.target) curvature = 1 + position * 0.3
      else if (samePair.length > 1) curvature = 0.3 + position * 0.2
      return {
        source: edge.source,
        target: edge.target,
        type: edge.relationship,
        curvature,
        looseDetails: edge.looseDetails,
        strictDetails: edge.strictDetails
      }
    })
    return withOffsets
  }, [edges])

  const graphData = useMemo(() => ({ nodes, links }), [nodes, links])

  // ---- forces like Create Schema
  useEffect(() => {
    if (!graphRef.current) return
    const fg = graphRef.current
    const linkForce = fg.d3Force("link")
    if (linkForce) linkForce.distance(() => 190)
    const charge = fg.d3Force("charge")
    if (charge) charge.strength(-520)
    fg.d3Force(
      "collide",
      forceCollide((node) => (node._r || 40) + 24).strength(1).iterations(2)
    )
    fg.d3ReheatSimulation()
  }, [graphData])

  // ---- stable node palette (like Create Schema)
  const getNodeColor = useMemo(() => {
    const base = [
      "#ef4444","#3b82f6","#8b5cf6","#ec4899","#10b981",
      "#f59e0b","#14b8a6","#22d3ee","#a3e635","#f97316",
      "#6366f1","#f43f5e","#06b6d4","#34d399","#fbbf24",
      "#e879f9","#60a5fa","#34d399","#f87171","#fcd34d",
      "#a78bfa","#f472b6","#06b6d4","#f59e0b","#fb7185"
    ]
    const map = new Map()
    let i = 0
    return (name) => {
      if (!map.has(name)) map.set(name, base[i++ % base.length])
      return map.get(name)
    }
  }, [])

  // ---- tooltips adapted to your data shape
  const formatNodeTooltip = (node) => {
    const t = nodeTypes.find(nt => nt.name === node.id)
    const loose = t?.looseDetails || []
    const strict = t?.strictDetails || [] // in your pipeline these originate from "optionalProperties"
    const optionalSet = new Set(strict.map(p => `${p.name}:${p.type}`))

    const rows =
      loose.length
        ? loose.map(p => {
            const key = `${p.name}:${p.type}`
            const opt = optionalSet.has(key)
            return `<div class="row" style="${tooltipRowStyle}">
              <span class="k" style="${tooltipKeyStyle}">${p.name}</span>
              <span class="t" style="${tooltipTypeStyle}">${p.type}</span>
              ${opt ? `<span class="o" style="${tooltipOptionalStyle}">optional</span>` : ``}
            </div>`
          }).join("")
        : `<div class="empty" style="${tooltipEmptyStyle}">-</div>`

    return `
      <div style="
        ${tooltipCardStyle}">
        <div style="font-weight:600; margin-bottom:6px; overflow-wrap:anywhere;">${node.id}</div>
        ${rows}
      </div>`
  }

  const formatLinkTooltip = (l) => {
    const loose = l.looseDetails || []
    const strict = l.strictDetails || []
    const optionalSet = new Set(strict.map(p => `${p.name}:${p.type}`))

    const rows =
      loose.length
        ? loose.map(p => {
            const key = `${p.name}:${p.type}`
            const opt = optionalSet.has(key)
            return `<div class="row" style="${tooltipRowStyle}">
              <span class="k" style="${tooltipKeyStyle}">${p.name}</span>
              <span class="t" style="${tooltipTypeStyle}">${p.type}</span>
              ${opt ? `<span class="o" style="${tooltipOptionalStyle}">optional</span>` : ``}
            </div>`
          }).join("")
        : `<div class="empty" style="${tooltipEmptyStyle}">-</div>`

    return `
      <div style="
        ${tooltipCardStyle}">
        <div style="font-weight:600; margin-bottom:6px; overflow-wrap:anywhere;">${l.type || "Edge"}</div>
        ${rows}
      </div>`
  }

  // ---- node painter like Create Schema (inner label)
  const drawNodeWithInnerLabel = (node, ctx, globalScale) => {
    const fullLabel = String(node.id || "")
    const fontSize = Math.max(8, Math.min(14, 12 / globalScale))
    ctx.font = `${fontSize}px Sans-Serif`

    const maxTextWidth = MAX_NODE_RADIUS * 2 - 12
    let label = fullLabel
    if (ctx.measureText(label).width > maxTextWidth) {
      let lo = 0
      let hi = fullLabel.length
      while (lo < hi) {
        const mid = Math.floor((lo + hi + 1) / 2)
        const candidate = `${fullLabel.slice(0, mid)}...`
        if (ctx.measureText(candidate).width <= maxTextWidth) lo = mid
        else hi = mid - 1
      }
      label = lo > 0 ? `${fullLabel.slice(0, lo)}...` : "..."
    }

    const textW = ctx.measureText(label).width
    const r = Math.min(MAX_NODE_RADIUS, Math.max(MIN_NODE_RADIUS, textW / 2 + 8))
    node._r = r

    ctx.beginPath()
    ctx.arc(node.x, node.y, r, 0, 2 * Math.PI, false)
    ctx.fillStyle = getNodeColor(node.id)
    ctx.fill()

    ctx.textAlign = "center"
    ctx.textBaseline = "middle"
    ctx.lineWidth = Math.max(1, 3 / globalScale)
    ctx.strokeStyle = "rgba(0,0,0,0.25)"
    ctx.strokeText(label, node.x, node.y)
    ctx.fillStyle = "#fff"
    ctx.fillText(label, node.x, node.y)
  }

  const paintNodePointerArea = (node, color, ctx) => {
    const r = node._r || 14
    ctx.beginPath()
    ctx.arc(node.x, node.y, r, 0, 2 * Math.PI, false)
    ctx.fillStyle = color
    ctx.fill()
  }

  // ---- controls
  const zoomBy = (k = 1.2) => {
    const fg = graphRef.current
    if (!fg) return
    const curr = typeof fg.zoom === "function" ? fg.zoom() : 1
    fg.zoom(curr * k, 400)
  }
  const zoomToFit = () => graphRef.current?.zoomToFit?.(500, 80)
  const centerAt = () => graphRef.current?.centerAt?.(0, 0, 400)

  return (
    <div className="w-3/5 p-4 pt-14 relative bg-white dark:bg-gray-900">
      {/* inline controls, same look/feel */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2">
        <button
          onClick={() => zoomBy(1 / 1.2)}
          className="px-3 py-1.5 rounded bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          -
        </button>
        <button
          onClick={() => zoomBy(1.2)}
          className="px-3 py-1.5 rounded bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          +
        </button>
        <button
          onClick={zoomToFit}
          className="px-3 py-1.5 rounded bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          Fit
        </button>
        <button
          onClick={centerAt}
          className="px-3 py-1.5 rounded bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          Center
        </button>
      </div>

      <ForceGraph2D
        ref={graphRef}
        graphData={graphData}
        width={width}
        height={height}
        // styling to match Create Schema
        nodeRelSize={4}
        nodeResolution={16}
        nodeColor={(n) => getNodeColor(n.id)}
        nodeLabel={(n) => formatNodeTooltip(n)}
        linkColor={() => "rgba(100,100,100,0.5)"}
        linkWidth={1}
        linkDirectionalArrowLength={5}
        linkDirectionalArrowRelPos={0.94}
        linkLabel={(l) => formatLinkTooltip(l)}
        linkCurvature={(link) => link.curvature || (link.source === link.target ? 0.6 : 0)}
        d3AlphaDecay={0.03}
        d3VelocityDecay={0.5}
        enableZoomPanInteraction={true}
        minZoom={0.1}
        maxZoom={8}
        nodeCanvasObject={(node, ctx, globalScale) => drawNodeWithInnerLabel(node, ctx, globalScale)}
        nodePointerAreaPaint={paintNodePointerArea}
        linkCanvasObjectMode={() => "after"}
        linkCanvasObject={(link, ctx, globalScale) => {
          if (!link.type) return
          const s = link.source
          const t = link.target
          if (!s || !t || s.x == null || t.x == null) return

          // midpoint for label (works with curvature visually OK for small bends)
          const mx = (s.x + t.x) / 2
          const my = (s.y + t.y) / 2

          const label = String(link.type)
          const fontSize = 11 / globalScale

          const prevAlpha = ctx.globalAlpha
          ctx.globalAlpha = 1

          ctx.font = `${fontSize}px 'Inter', sans-serif`
          ctx.textAlign = "center"
          ctx.textBaseline = "middle"

          const textWidth = ctx.measureText(label).width
          const padX = 6, padY = 3

          const bgColor = isDark ? "rgba(31,41,55,0.9)" : "rgba(255,255,255,0.95)"
          const borderColor = isDark ? "rgba(255,255,255,0.15)" : "rgba(17,24,39,0.1)"
          const textColor = isDark ? "#f9fafb" : "#111827"

          ctx.fillStyle = bgColor
          ctx.strokeStyle = borderColor
          ctx.lineWidth = 1 / globalScale
          if (typeof ctx.roundRect === "function") {
            ctx.beginPath()
            ctx.roundRect(
              mx - textWidth / 2 - padX,
              my - fontSize / 2 - padY,
              textWidth + padX * 2,
              fontSize + padY * 2,
              6 / globalScale
            )
            ctx.fill()
            ctx.stroke()
          } else {
            // fallback for older canvases
            ctx.fillRect(
              mx - textWidth / 2 - padX,
              my - fontSize / 2 - padY,
              textWidth + padX * 2,
              fontSize + padY * 2
            )
            ctx.strokeRect(
              mx - textWidth / 2 - padX,
              my - fontSize / 2 - padY,
              textWidth + padX * 2,
              fontSize + padY * 2
            )
          }

          ctx.fillStyle = textColor
          ctx.fillText(label, mx, my)

          ctx.globalAlpha = prevAlpha
        }}
      />
    </div>
  )
}
